export function createDashboardCore({ dashboardItems, dashboardCount, dashboardEmpty, drawLine, animSpeed }) {
  let dashboardAnimFrameId = null;
  let dashboardAnimLast = null;

  // Seuls les mini-aperçus à l'écran sont redessinés (même principe que la
  // galerie) : avant, la boucle redessinait les 116 cartes à chaque image,
  // même hors écran (~36 images/s mesurées sur l'onglet Dashboard). Les
  // canvas sont observés à la volée par la boucle -- aucune dépendance à la
  // création des cartes -- et oubliés par stopDashMiniAnim (suppression ou
  // regénération d'une carte ; ré-observés au passage suivant si besoin).
  const observedCanvases = new WeakSet();
  const visibleCanvases = new WeakSet();
  const visibilityObs = typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visibleCanvases.add(entry.target);
        else visibleCanvases.delete(entry.target);
      }
    }, { rootMargin: '100px 0px' })
    : null;

  function isCanvasVisible(canvas) {
    if (!visibilityObs) return true;
    if (!observedCanvases.has(canvas)) {
      observedCanvases.add(canvas);
      visibilityObs.observe(canvas);
    }
    return visibleCanvases.has(canvas);
  }

  function updateDashCount() {
    const count = dashboardItems.length;
    dashboardCount.textContent = count + ' punchline' + (count > 1 ? 's' : '');
    dashboardEmpty.hidden = count > 0;
  }

  function resizeDashMiniCanvas(item) {
    if (!item.canvas || !item.data) return;
    const dpr = window.devicePixelRatio || 1;
    const cell = item.canvas.parentElement;
    const width = cell ? (cell.clientWidth || 220) : 220;
    const height = Math.round(item.data.canvasH / item.data.pw * width);
    item.canvas.width = width * dpr;
    item.canvas.height = height * dpr;
    item.canvas.style.width = width + 'px';
    item.canvas.style.height = height + 'px';
  }

  function stopAllDashAnims() {
    if (dashboardAnimFrameId) {
      cancelAnimationFrame(dashboardAnimFrameId);
      dashboardAnimFrameId = null;
    }
    dashboardAnimLast = null;
  }

  // La boucle partagée s'arrête d'elle-même quand le dashboard est vide ;
  // ici on oublie seulement le canvas de la carte (cf. isCanvasVisible).
  function stopDashMiniAnim(item) {
    const canvas = item?.canvas;
    if (!canvas || !visibilityObs) return;
    visibilityObs.unobserve(canvas);
    observedCanvases.delete(canvas);
    visibleCanvases.delete(canvas);
  }

  function startDashMiniAnim(_item) {
    if (dashboardAnimFrameId) return;

    function loop(ts) {
      if (!dashboardAnimLast) dashboardAnimLast = ts;
      const dt = Math.min((ts - dashboardAnimLast) / 1000, 0.05);
      dashboardAnimLast = ts;

      for (const item of dashboardItems) {
        if (!item.canvas || !item.data) continue;
        const pw = item.data.pw;
        item.tX += animSpeed * dt;
        if (item.tX >= pw) item.tX -= pw;
        item.rX -= animSpeed * dt;
        if (item.rX <= -pw) item.rX += pw;
        if (isCanvasVisible(item.canvas)) drawLine(item);
      }

      if (dashboardItems.length > 0) {
        dashboardAnimFrameId = requestAnimationFrame(loop);
      } else {
        dashboardAnimFrameId = null;
        dashboardAnimLast = null;
      }
    }

    dashboardAnimFrameId = requestAnimationFrame(loop);
  }

  return {
    updateDashCount,
    resizeDashMiniCanvas,
    stopAllDashAnims,
    stopDashMiniAnim,
    startDashMiniAnim,
  };
}
